import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLD from "@/components/feature/JsonLD";
import { getAnimeById, getRelatedAnime } from "@/mocks/animeData";
import { getArticleById, getRelatedArticles } from "@/mocks/articlesData";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const numId = parseInt(id || "0", 10);

  const anime = getAnimeById(numId);
  const article = getArticleById(numId);

  const item = anime || article;
  const relatedAnime = anime ? getRelatedAnime(numId, 6) : [];
  const relatedArticles = article ? getRelatedArticles(numId, 3) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的内容不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isAnime = anime !== undefined;
  const title = isAnime ? anime.title : (article ? article.title : "");
  const cover = isAnime ? anime.cover : (article ? article.cover : "");

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://naishuidongman.com",
      },
      ...(isAnime
        ? [
            {
              "@type": "ListItem" as const,
              "position": 2,
              "name": `${anime.category}动漫`,
              "item": `https://naishuidongman.com/category/${getSlugFromCategory(anime.category)}`,
            },
          ]
        : [
            {
              "@type": "ListItem" as const,
              "position": 2,
              "name": "动漫资讯",
              "item": "https://naishuidongman.com",
            },
          ]),
      {
        "@type": "ListItem",
        "position": isAnime ? 3 : 2,
        "name": title,
      },
    ],
  };

  const articleSchema = isAnime
    ? {
        "@context": "https://schema.org",
        "@type": "Movie",
        "name": anime.title,
        "description": anime.description,
        "image": anime.cover,
        "datePublished": `${anime.year}`,
        "productionCompany": {
          "@type": "Organization",
          "name": anime.studio,
        },
      }
    : {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article?.title || "",
        "description": article?.excerpt || "",
        "image": article?.cover || "",
        "datePublished": article?.date || "",
        "author": {
          "@type": "Organization",
          "name": article?.author || "奶水动漫编辑部",
        },
      };

  function getSlugFromCategory(cat: string): string {
    const map: Record<string, string> = {
      "热血": "rexue",
      "恋爱": "lianai",
      "搞笑": "gaoxiao",
      "奇幻": "qihuan",
    };
    return map[cat] || "rexue";
  }

  return (
    <>
      <JsonLD data={breadcrumbSchema} />
      <JsonLD data={articleSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                ...(isAnime
                  ? [
                      {
                        label: `${anime.category}动漫`,
                        to: `/category/${getSlugFromCategory(anime.category)}`,
                      },
                    ]
                  : [{ label: "动漫资讯" }]),
                { label: title },
              ]}
            />

            <article className="py-6">
              {isAnime ? (
                <>
                  <div className="flex flex-col lg:flex-row gap-8 mb-12">
                    <div className="w-full lg:w-[300px] flex-shrink-0">
                      <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                        <img
                          src={anime.cover}
                          alt={anime.title}
                          title={`${anime.title} - 奶水动漫在线高清在线观看`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                        {anime.title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-sm font-semibold">
                          {anime.score} 分
                        </span>
                        <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                          {anime.episodes}
                        </span>
                        <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                          {anime.year}
                        </span>
                        <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                          {anime.studio}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {anime.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="bg-background-100/70 rounded-xl p-5 mb-6">
                        <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                          剧情简介
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {anime.description}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                          <i className="ri-play-circle-fill text-lg"></i>
                          立即观看
                        </button>
                        <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/80 transition-all cursor-pointer whitespace-nowrap">
                          <i className="ri-heart-line"></i>
                          收藏
                        </button>
                        <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/80 transition-all cursor-pointer whitespace-nowrap">
                          <i className="ri-share-line"></i>
                          分享
                        </button>
                      </div>
                    </div>
                  </div>

                  {relatedAnime.length > 0 && (
                    <section className="pb-14">
                      <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                        <a href="#related">相关推荐</a>
                      </h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                        {relatedAnime.map((rel) => (
                          <Link
                            key={rel.id}
                            to={`/article/${rel.id}`}
                            className="group cursor-pointer"
                          >
                            <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                              <img
                                src={rel.cover}
                                alt={rel.title}
                                title={rel.title}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                                {rel.score}
                              </div>
                            </div>
                            <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                              {rel.title}
                            </h3>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              ) : article ? (
                <>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 mb-6 pb-6 border-b border-background-200/70">
                    <span className="text-sm text-foreground-500">
                      <i className="ri-user-line mr-1"></i>
                      {article.author}
                    </span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-calendar-line mr-1"></i>
                      {article.date}
                    </span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-eye-line mr-1"></i>
                      {article.readCount} 阅读
                    </span>
                  </div>

                  <div className="mb-8 rounded-xl overflow-hidden">
                    <img
                      src={article.cover}
                      alt={article.title}
                      title={article.title}
                      className="w-full aspect-[2/1] object-cover object-top"
                    />
                  </div>

                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed mb-12"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="flex flex-wrap gap-2 mb-12 pt-6 border-t border-background-200/70">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {relatedArticles.length > 0 && (
                    <section className="pb-14">
                      <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                        <a href="#related">相关文章</a>
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {relatedArticles.map((rel) => (
                          <Link
                            key={rel.id}
                            to={`/article/${rel.id}`}
                            className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/60 transition-all cursor-pointer"
                          >
                            <div className="aspect-[16/9] overflow-hidden bg-background-200">
                              <img
                                src={rel.cover}
                                alt={rel.title}
                                title={rel.title}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="p-4">
                              <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                                {rel.title}
                              </h3>
                              <p className="text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              ) : null}
            </article>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}